"use client"

import { useState } from "react"
import { cn } from "@/lib/utils"
import { Bot, User, ChevronDown, Wrench } from "lucide-react"
import { MarkdownRenderer } from "./markdown-renderer"
import { MessageActions } from "./message-actions"
import { ToolResultCard } from "./tool-result-card"
import { ThinkingIndicator } from "./thinking-indicator"
import type { ChatMessage as ChatMessageType, ToolCallRecord } from "@/lib/ai-chat/types"

interface ChatMessageProps {
  message: ChatMessageType
  isLast: boolean
  isStreaming?: boolean
  onRegenerate?: () => void
  onEdit?: () => void
  compact?: boolean
  conversationId?: string | null
}

export function ChatMessage({
  message,
  isLast,
  isStreaming,
  onRegenerate,
  onEdit,
  compact,
  conversationId,
}: ChatMessageProps) {
  const isUser = message.role === "user"
  const [toolsExpanded, setToolsExpanded] = useState(false)

  return (
    <div
      className={cn(
        "group flex",
        isUser ? "flex-row-reverse" : "flex-row",
        compact ? "gap-1.5 px-2 py-1" : "gap-3 px-4 py-3"
      )}
    >
      {/* Avatar — animated thinking orb when streaming, static icon otherwise */}
      {!isUser && isStreaming ? (
        <ThinkingIndicator size={compact ? "sm" : "md"} className="mt-0.5" />
      ) : (
        <div
          className={cn(
            "flex items-center justify-center rounded-full shrink-0 mt-0.5",
            compact ? "h-5 w-5" : "h-8 w-8",
            isUser ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
          )}
        >
          {isUser
            ? <User className={compact ? "h-2.5 w-2.5" : "h-4 w-4"} />
            : <Bot className={compact ? "h-2.5 w-2.5" : "h-4 w-4"} />
          }
        </div>
      )}

      {/* Content */}
      <div
        className={cn(
          "flex flex-col min-w-0 overflow-hidden",
          compact ? "gap-0.5 max-w-[92%]" : "gap-1.5 max-w-[85%]",
          isUser ? "items-end" : "items-start"
        )}
      >
        {/* Tool calls — compact collapsible summary */}
        {!isUser && message.toolCalls && message.toolCalls.length > 0 && (
          <div className="w-full">
            <button
              type="button"
              onClick={() => setToolsExpanded((v) => !v)}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors py-0.5"
            >
              <Wrench className="h-3 w-3 shrink-0" />
              <span>
                Used {message.toolCalls.length} tool{message.toolCalls.length > 1 ? "s" : ""}
              </span>
              <ChevronDown className={cn("h-3 w-3 transition-transform", toolsExpanded && "rotate-180")} />
            </button>
            {toolsExpanded && (
              <div className="pl-4 border-l border-border/50 mt-1 space-y-0">
                {message.toolCalls.map((tc: ToolCallRecord) => (
                  <ToolResultCard
                    key={tc.id}
                    toolName={tc.toolName}
                    status={tc.status}
                    success={tc.result?.success}
                    summary={tc.summary || (tc.result?.success ? undefined : tc.result?.error)}
                  />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Message bubble */}
        {message.content && (
          <div
            className={cn(
              "overflow-hidden max-w-full",
              compact ? "rounded-xl px-2.5 py-1.5" : "rounded-2xl px-4 py-2.5",
              isUser
                ? "bg-primary text-primary-foreground rounded-br-md"
                : "bg-muted text-foreground rounded-bl-md"
            )}
          >
            {isUser ? (
              <p className={cn("whitespace-pre-wrap break-words", compact ? "text-[13px] leading-normal" : "text-sm")}>{message.content}</p>
            ) : (
              <MarkdownRenderer content={message.content} className={compact ? "pip-compact-prose" : undefined} />
            )}

            {/* Streaming cursor */}
            {isStreaming && !isUser && (
              <span className="inline-block w-2 h-4 bg-foreground/60 animate-pulse ml-0.5 -mb-0.5" />
            )}
          </div>
        )}

        {/* Actions */}
        <MessageActions
          content={message.content}
          role={message.role}
          isLast={isLast}
          messageId={message.id}
          conversationId={conversationId}
          onRegenerate={onRegenerate}
          onEdit={onEdit}
        />
      </div>
    </div>
  )
}
